// src/lib/deferredSdk.ts
import logger from './logger';

type SdkAction<T> = (sdk: T) => void;

interface DeferredSdk<T> {
  /** Runs now if the SDK is ready, queues until it is, and no-ops if it failed to load. */
  run(action: SdkAction<T>): void;
  /** Loads the SDK once and replays queued actions in the order they were made. */
  load(): Promise<void>;
}

/**
 * Wraps an SDK that lives in its own chunk so callers can use it from startup
 * without pulling it into the eager bundle. A failed load (blocked by an ad
 * blocker, say) drops the queue and never surfaces an error to the app.
 */
export function createDeferredSdk<T>(name: string, loadSdk: () => Promise<T>): DeferredSdk<T> {
  let sdk: T | null = null;
  let failed = false;
  let loading: Promise<void> | null = null;
  let queue: SdkAction<T>[] = [];

  const execute = (loaded: T, action: SdkAction<T>) => {
    try {
      action(loaded);
    } catch (error) {
      logger.warn(`${name}: SDK call failed`, error);
    }
  };

  return {
    run(action) {
      if (sdk) {
        execute(sdk, action);
      } else if (!failed) {
        queue.push(action);
      }
    },
    load() {
      loading ??= Promise.resolve()
        .then(loadSdk)
        .then((loaded) => {
          sdk = loaded;
          const pending = queue;
          queue = [];
          pending.forEach((action) => execute(loaded, action));
        })
        .catch((error) => {
          failed = true;
          queue = [];
          logger.warn(`${name}: failed to load, events will be dropped`, error);
        });
      return loading;
    },
  };
}

/** Runs a task once the browser is idle after startup, or after `timeoutMs` at the latest. */
export function runWhenIdle(task: () => void, timeoutMs = 2000): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(task, { timeout: timeoutMs });
  } else {
    // Safari has no requestIdleCallback.
    window.setTimeout(task, Math.min(timeoutMs, 1000));
  }
}
